import React, { useEffect, useMemo, useRef, useState } from 'react';
import { textIncludesQuery } from '@narrativebench/editor-core';
import { cn } from '@/lib/utils';
import { Input } from './ui/input';
import { useLocale } from '../contexts/LocaleContext';
import type { Scene } from './Scenes';

interface SceneTitleAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  scenes: Scene[];
  onSelectScene: (scene: Scene) => void;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
}

export const SceneTitleAutocomplete: React.FC<SceneTitleAutocompleteProps> = ({
  value,
  onChange,
  scenes,
  onSelectScene,
  placeholder,
  disabled = false,
  autoFocus = false,
}) => {
  const { t } = useLocale();
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const filteredScenes = useMemo(() => {
    if (!value.trim()) return scenes;
    return scenes.filter((scene) => textIncludesQuery(scene.title, value));
  }, [scenes, value]);

  useEffect(() => {
    setHighlightedIndex((current) => {
      if (filteredScenes.length === 0) return 0;
      return Math.min(current, filteredScenes.length - 1);
    });
  }, [filteredScenes]);

  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (event: MouseEvent): void => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const option = listRef.current?.querySelector<HTMLElement>(`[data-index="${highlightedIndex}"]`);
    if (typeof option?.scrollIntoView === 'function') {
      option.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex, open]);

  const selectScene = (scene: Scene): void => {
    onSelectScene(scene);
    setOpen(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();
      if (!open) {
        setOpen(true);
        setHighlightedIndex(0);
        return;
      }
      if (filteredScenes.length === 0) return;
      setHighlightedIndex((current) => (current + 1) % filteredScenes.length);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();
      if (!open) {
        setOpen(true);
        setHighlightedIndex(Math.max(filteredScenes.length - 1, 0));
        return;
      }
      if (filteredScenes.length === 0) return;
      setHighlightedIndex((current) =>
        current <= 0 ? filteredScenes.length - 1 : current - 1,
      );
      return;
    }
    if (event.key === 'Enter' && open) {
      event.preventDefault();
      event.stopPropagation();
      const scene = filteredScenes[highlightedIndex];
      if (scene) selectScene(scene);
      return;
    }
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <Input
        type="text"
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={open}
        role="combobox"
        autoFocus={autoFocus}
        onChange={(event) => {
          onChange(event.target.value);
          setOpen(true);
          setHighlightedIndex(0);
        }}
        onKeyDown={handleKeyDown}
      />
      {open && (
        <div
          ref={listRef}
          role="listbox"
          className="absolute z-50 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md"
        >
          {filteredScenes.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">{t('no_scenes')}</div>
          ) : (
            filteredScenes.map((scene, index) => (
              <button
                key={scene.id}
                type="button"
                role="option"
                data-index={index}
                aria-selected={index === highlightedIndex}
                className={cn(
                  'flex w-full rounded px-2 py-1.5 text-left text-sm hover:bg-accent',
                  index === highlightedIndex && 'bg-accent',
                )}
                onMouseEnter={() => setHighlightedIndex(index)}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => selectScene(scene)}
              >
                {scene.title}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};
